/* eslint-disable max-lines */

import { Directionality } from '@angular/cdk/bidi';
import {
  CdkConnectedOverlay,
  CdkOverlayOrigin,
  ConnectedPosition,
  ConnectionPositionPair,
  FlexibleConnectedPositionStrategyOrigin,
  Overlay,
} from '@angular/cdk/overlay';
import {
  ChangeDetectorRef,
  Directive,
  Host,
  Inject,
  Input,
  Optional,
  SimpleChanges,
  TemplateRef,
  ViewContainerRef,
  ɵDirectiveDef as DirectiveDef,
  ɵNG_DIR_DEF as NG_DIR_DEF,
} from '@angular/core';

import { coalesceChangeDetection } from '@recruitee/utils';

import { RtOverlayRef } from './overlay-ref';
import { RtOverlayConfig } from './overlay-state';

// First word described where the dropdown will land,
// Second word describes its align
// F.e. "BottomLeft" means menu will appear in the bottom and will be aligned to the left
// F.e. "RightTop" means menu will appear in the right and will be aligned to the top
export enum OverlayPosition {
  BottomLeft = 'bottomleft',
  BottomRight = 'bottomright',
  TopLeft = 'topleft',
  TopRight = 'topright',
  RightBottom = 'rightbottom',
  RightTop = 'righttop',
  LeftBottom = 'leftbottom',
  LeftTop = 'lefttop',
}

/**
 * Directive applied to an element to make it usable as an origin for an Overlay using a
 * ConnectedPositionStrategy.
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[rt-overlay-origin]',
  exportAs: 'rtOverlayOrigin',
})
export class OverlayOriginDirective extends CdkOverlayOrigin {}

/** Default set of positions for the overlay. Follows the behavior of a dropdown. */
export const positionPairs = {
  [OverlayPosition.BottomLeft]: new ConnectionPositionPair(
    { originX: 'start', originY: 'bottom' },
    { overlayX: 'start', overlayY: 'top' },
  ),
  [OverlayPosition.BottomRight]: new ConnectionPositionPair(
    { originX: 'end', originY: 'bottom' },
    { overlayX: 'end', overlayY: 'top' },
  ),
  [OverlayPosition.TopLeft]: new ConnectionPositionPair(
    { originX: 'start', originY: 'top' },
    { overlayX: 'start', overlayY: 'bottom' },
  ),
  [OverlayPosition.TopRight]: new ConnectionPositionPair(
    { originX: 'end', originY: 'top' },
    { overlayX: 'end', overlayY: 'bottom' },
  ),
  [OverlayPosition.LeftBottom]: new ConnectionPositionPair(
    { originX: 'start', originY: 'bottom' },
    { overlayX: 'end', overlayY: 'bottom' },
  ),
  [OverlayPosition.LeftTop]: new ConnectionPositionPair(
    { originX: 'start', originY: 'top' },
    { overlayX: 'end', overlayY: 'top' },
  ),
  [OverlayPosition.RightBottom]: new ConnectionPositionPair(
    { originX: 'end', originY: 'bottom' },
    { overlayX: 'start', overlayY: 'bottom' },
  ),
  [OverlayPosition.RightTop]: new ConnectionPositionPair(
    { originX: 'end', originY: 'top' },
    { overlayX: 'start', overlayY: 'top' },
  ),
};

const defaultPositions = {
  [OverlayPosition.BottomLeft]: [
    positionPairs[OverlayPosition.BottomLeft],
    positionPairs[OverlayPosition.TopLeft],
  ],
  [OverlayPosition.BottomRight]: [
    positionPairs[OverlayPosition.BottomRight],
    positionPairs[OverlayPosition.TopRight],
  ],
  [OverlayPosition.TopLeft]: [
    positionPairs[OverlayPosition.TopLeft],
    positionPairs[OverlayPosition.BottomLeft],
  ],
  [OverlayPosition.TopRight]: [
    positionPairs[OverlayPosition.TopRight],
    positionPairs[OverlayPosition.BottomRight],
  ],
  [OverlayPosition.RightBottom]: [
    positionPairs[OverlayPosition.RightBottom],
    positionPairs[OverlayPosition.RightTop],
  ],
  [OverlayPosition.RightTop]: [
    positionPairs[OverlayPosition.RightTop],
    positionPairs[OverlayPosition.RightBottom],
  ],
  [OverlayPosition.LeftBottom]: [
    positionPairs[OverlayPosition.LeftBottom],
    positionPairs[OverlayPosition.LeftTop],
  ],
  [OverlayPosition.LeftTop]: [
    positionPairs[OverlayPosition.LeftTop],
    positionPairs[OverlayPosition.LeftBottom],
  ],
};

@Directive({ selector: 'rt-modified-cdk-connected-overlay', standalone: true })
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export class ModifiedCdkConnectedOverlay extends CdkConnectedOverlay {
  constructor(
    overlay: Overlay,
    // Optional is required for our ComponentTooltip to work in current implementation
    @Optional() templateRef: TemplateRef<any>,
    viewContainerRef: ViewContainerRef,
    dir: Directionality,
  ) {
    super(overlay, templateRef, viewContainerRef, () => overlay.scrollStrategies.reposition(), dir);
  }
}

const cdkConnectedOverlayDirDef: DirectiveDef<CdkConnectedOverlay> = (<any>(
  ModifiedCdkConnectedOverlay
))[NG_DIR_DEF];

@Directive({
  standalone: true,
  selector: '[rt-connected-overlay]',
  exportAs: 'rtConnectedOverlay',
  hostDirectives: [
    {
      directive: ModifiedCdkConnectedOverlay,
      inputs: [
        'cdkConnectedOverlayOrigin: origin',
        'cdkConnectedOverlayPositions: positions',
        'cdkConnectedOverlayPositionStrategy: positionStrategy',
        'cdkConnectedOverlayOffsetX: offsetX',
        'cdkConnectedOverlayOffsetY: offsetY',
        'cdkConnectedOverlayWidth: width',
        'cdkConnectedOverlayHeight: height',
        'cdkConnectedOverlayMinWidth: minWidth',
        'cdkConnectedOverlayMinHeight: minHeight',
        'cdkConnectedOverlayBackdropClass: backdropClass',
        'cdkConnectedOverlayPanelClass: panelClass',
        'cdkConnectedOverlayViewportMargin: viewportMargin',
        'cdkConnectedOverlayScrollStrategy: scrollStrategy',
        'cdkConnectedOverlayOpen: open',
        'cdkConnectedOverlayDisableClose: disableClose',
        'cdkConnectedOverlayTransformOriginOn: transformOriginSelector',
        'cdkConnectedOverlayHasBackdrop: hasBackdrop',
        'cdkConnectedOverlayLockPosition: lockPosition',
        'cdkConnectedOverlayFlexibleDimensions: flexibleDimensions',
        'cdkConnectedOverlayGrowAfterOpen: growAfterOpen',
        'cdkConnectedOverlayPush: push',
      ],
      // eslint-disable-next-line @angular-eslint/no-outputs-metadata-property
      outputs: [
        'backdropClick',
        'positionChange',
        'attach',
        'detach',
        'overlayKeydown',
        'overlayOutsideClick',
      ],
    },
  ],
})
export class ConnectedOverlayDirective {
  public backdropClick = this.cdkConnectedOverlay.backdropClick;
  public positionChange = this.cdkConnectedOverlay.positionChange;
  public attach = this.cdkConnectedOverlay.attach;
  public detach = this.cdkConnectedOverlay.detach;
  public overlayKeydown = this.cdkConnectedOverlay.overlayKeydown;
  public overlayOutsideClick = this.cdkConnectedOverlay.overlayOutsideClick;

  @Input()
  public positions: ConnectedPosition[];

  public position: string[] | OverlayPosition = [
    OverlayPosition.BottomRight,
    OverlayPosition.BottomLeft,
    OverlayPosition.TopRight,
    OverlayPosition.TopLeft,
  ];

  public set origin(value: CdkOverlayOrigin | FlexibleConnectedPositionStrategyOrigin) {
    cdkConnectedOverlayDirDef.setInput!(
      this.cdkConnectedOverlay,
      value,
      'cdkConnectedOverlayOrigin',
      'origin',
    );
    this.parentDetectChanges();
  }

  public get open() {
    return this.cdkConnectedOverlay.open;
  }
  public set open(newValue: boolean) {
    this.setOverlayOpen(newValue);
    this.parentDetectChanges();
  }

  @Input() public rtConnectedOverlayPosition: string[] | OverlayPosition;
  @Input() public rtConnectedOverlayAutoClose: boolean;

  public autoCloseOnUnpreventedClick: boolean;

  private parentDetectChanges = coalesceChangeDetection(() => this.parentCdRef.detectChanges());

  constructor(
    public cdRef: ChangeDetectorRef,
    public cdkConnectedOverlay: ModifiedCdkConnectedOverlay,
    @Host() private parentCdRef: ChangeDetectorRef,
    @Inject('overlayLevel') private overlayLevel: number,
  ) {
    (<any>cdkConnectedOverlay).overlayLevel = overlayLevel;
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.positions || changes.rtConnectedOverlayPosition) {
      this.overlayUpdatePositions();
    }
  }

  public openAt(origin: OverlayOriginDirective): void {
    this.origin = origin;
    this.toggleOverlay(true);
  }

  public recreateOverlay(): void {
    this.destroyOverlay();
    this.cdkConnectedOverlay['_createOverlay']();
  }

  public updatePosition(): void {
    if (this.open) {
      // TODO instead of recreating the overlay, just update its' position in-place
      this.toggleOverlay(false);
      this.toggleOverlay(true);
    }
  }

  public updatePositionWithoutRedrawing(): void {
    if (this.open) {
      const overlayRef = <RtOverlayRef>this.cdkConnectedOverlay['_overlayRef'];
      if (overlayRef && overlayRef.attachResult) {
        overlayRef.updateSize({});
        overlayRef.updatePosition();
      }
    }
  }

  public destroyOverlay(): void {
    if (this.cdkConnectedOverlay['_overlayRef']) {
      this.cdkConnectedOverlay['_overlayRef'].dispose();
      this.cdkConnectedOverlay['_overlayRef'] = null;
      this.cdkConnectedOverlay['_position'] = null;
    }

    if (this.cdkConnectedOverlay['_backdropSubscription']) {
      this.cdkConnectedOverlay['_backdropSubscription'].unsubscribe();
    }
    if (this.cdkConnectedOverlay['_positionSubscription']) {
      this.cdkConnectedOverlay['_positionSubscription'].unsubscribe();
    }
    if (this.cdkConnectedOverlay['_attachSubscription']) {
      this.cdkConnectedOverlay['_attachSubscription'].unsubscribe();
    }
    if (this.cdkConnectedOverlay['_detachSubscription']) {
      this.cdkConnectedOverlay['_detachSubscription'].unsubscribe();
    }
  }

  public toggleOverlay(value: boolean) {
    this.setOverlayOpen(value);
    this.cdkConnectedOverlay.ngOnChanges(
      (<any>this.cdkConnectedOverlay)['__ngSimpleChanges__'].current,
    );
    this.parentCdRef.detectChanges();
    return (<RtOverlayRef>this.cdkConnectedOverlay.overlayRef).attachResult;
  }

  public overlayUpdatePositions() {
    cdkConnectedOverlayDirDef.setInput!(
      this.cdkConnectedOverlay,
      this.resolvePositions(this.positions, this.rtConnectedOverlayPosition),
      'cdkConnectedOverlayPositions',
      'positions',
    );
  }

  private setOverlayOpen(value: boolean) {
    cdkConnectedOverlayDirDef.setInput!(
      this.cdkConnectedOverlay,
      value,
      'cdkConnectedOverlayOpen',
      'open',
    );
  }

  private resolvePositions(positions: ConnectedPosition[], position: OverlayPosition | string[]) {
    if (positions && positions.length) {
      return positions;
    } else if (Array.isArray(position)) {
      return position.reduce(
        (acc, positionKey) => [...acc, ...defaultPositions[<OverlayPosition>positionKey]],
        <ConnectionPositionPair[]>[],
      );
    } else {
      return defaultPositions[position];
    }
  }
}

const connectedOverlayDirectivePrototype = <any>CdkConnectedOverlay.prototype;
const { _buildConfig } = connectedOverlayDirectivePrototype;
connectedOverlayDirectivePrototype._buildConfig = function (this: CdkConnectedOverlay) {
  const result = <RtOverlayConfig>_buildConfig.apply(this);
  result.level = (<any>this).overlayLevel;
  if (!this.backdropClass) {
    result.backdropClass = 'blank-backdrop';
  }

  return result;
};
